/**
 * Een mislukte Mass Message opnieuw proberen (06-10-2026, Jordy).
 *
 * "Retry now" of "Retry at…" (TijdKiezer, standaard nu + 15 minuten), alleen
 * bij status `failed`, bevestigen met SEND. De server doet alle controles van
 * inplannen opnieuw en zet failed -> scheduled in één stap; een tweede retry
 * geeft 409. Gebruikt in de Agenda (beheer) en in "My scheduled" (eigen rij).
 *
 * `basis` is `/api/admin/conversations/mass-message/agenda` (beheer) of `/mine`.
 *
 * 07-10-2026 (Jordy): tijden in de tijdzone van de gebruiker (`zone`, met de
 * zone erbij); "Retry at…" leest de tijd in die zone.
 */
import { useState } from 'react'
import { Field, Modal } from './Modal'
import { ErrorNote, Loading, Seg } from './ui'
import { TijdKiezer } from './TijdKiezer'
import { useApi } from '../lib/useApi'
import { api } from '../lib/api'
import { AMSTERDAM, metZone, zetOm, zoneLabel, zoneNaarUtc } from '../lib/amsterdam'
import { standaardNieuwMoment, tijdFout } from '../lib/tijdkeuze'
import { useBijZoneWissel } from '../lib/tijdzone'

export interface Poging {
  scheduled_at: string | null
  started_at: string | null
  status: string
  error: string | null
  retried_by: string | null
  retried_at: string | null
}

/** De geschiedenis per poging, oudste eerst. */
export function PogingenLijst({ basis, id, zone = AMSTERDAM }: { basis: string; id: number; zone?: string }) {
  const q = useApi<{ attempts: Poging[] }>(`${basis}/${id}/attempts`, [basis, id])
  if (q.loading && !q.data) return <Loading />
  if (q.error) return <ErrorNote error={q.error} />
  const lijst = q.data?.attempts ?? []
  return (
    <ol className="pogingen" data-testid="pogingen" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
      {lijst.map((p, i) => (
        <li key={i} style={{ marginBottom: 4 }}>
          <span className="mono">{p.scheduled_at ? metZone(p.scheduled_at, zone) : '—'}</span>
          {' · '}<b>{p.status}</b>
          {p.error ? <span className="mut2"> · {p.error}</span> : null}
          {p.retried_by ? (
            <div className="mut2" style={{ fontSize: 12 }}>
              Retried by {p.retried_by}{p.retried_at ? ` on ${metZone(p.retried_at, zone)}` : ''}
            </div>
          ) : null}
        </li>
      ))}
    </ol>
  )
}

export function RetryMassaDialoog({ basis, id, echteFans, onClose, onKlaar, zone = AMSTERDAM }: {
  basis: string
  id: number
  echteFans: boolean
  /** IANA-tijdzone van de gebruiker (standaard Amsterdam). */
  zone?: string
  onClose: () => void
  onKlaar: () => void
}) {
  const [wanneer, setWanneer] = useState<'now' | 'at'>('now')
  const [moment, setMoment] = useState(() => standaardNieuwMoment(undefined, zone))
  useBijZoneWissel(zone, (van, naar) => setMoment((m) => zetOm(m, van, naar)))
  const [getypt, setGetypt] = useState('')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState('')
  const tijdProbleem = wanneer === 'at' ? tijdFout(moment.datum, moment.tijd, undefined, zone) : null
  const mag = !bezig && !tijdProbleem && (!echteFans || getypt.trim().toUpperCase() === 'SEND')

  async function verstuur() {
    setBezig(true)
    setFout('')
    try {
      await api.post(`${basis}/${id}/retry`, {
        when: wanneer,
        ...(wanneer === 'at' ? { scheduled_at: zoneNaarUtc(moment.datum, moment.tijd, zone) } : {}),
        confirm: getypt.trim().toUpperCase(),
      })
      onKlaar()
      onClose()
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    } finally {
      setBezig(false)
    }
  }

  return (
    <Modal title="Retry this Mass Message?" width={520} onClose={onClose}
           desc="All checks of scheduling run again (rights, method, minimum price, content still available)."
           footer={
             <>
               <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
               <button className="btn primary sm" type="button" disabled={!mag} data-testid="retry-verstuur"
                       onClick={() => { void verstuur() }}>
                 {bezig ? 'Retrying…' : wanneer === 'now' ? 'Retry now' : 'Retry at this time'}
               </button>
             </>
           }>
      <Seg options={[{ value: 'now', label: 'Retry now' }, { value: 'at', label: 'Retry at…' }]}
           value={wanneer} onChange={(v) => setWanneer(v === 'at' ? 'at' : 'now')} />
      {wanneer === 'at' ? (
        <div style={{ marginTop: 10 }}>
          <TijdKiezer datum={moment.datum} tijd={moment.tijd} naam="Retry" label={`Retry at (${zoneLabel(zone)})`} zone={zone}
                      onChange={(m) => setMoment(m)} />
          {tijdProbleem ? <div className="massa-slot-errors">{tijdProbleem}</div> : null}
        </div>
      ) : null}
      <div style={{ marginTop: 10 }}>
        <b style={{ fontSize: 12.5 }}>Attempts</b>
        <PogingenLijst basis={basis} id={id} zone={zone} />
      </div>
      {echteFans ? (
        <Field label="Type SEND to confirm" hint="This Mass Message goes to real fans.">
          <input className="inp" value={getypt} placeholder="SEND" data-testid="retry-send"
                 onChange={(e) => setGetypt(e.target.value)} />
        </Field>
      ) : null}
      {fout ? <div className="massa-slot-errors" role="alert">{fout}</div> : null}
    </Modal>
  )
}
